import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Chargement, EnTetePage, Etiquette, Message, Vide } from "@cabinet/charte";
import { dossiersApi, ErreurApi, type CreationDeDossier } from "../api/client";
import { useSession } from "../session";

export function PageDossiers() {
  const { peut } = useSession();
  const [formulaireOuvert, setFormulaireOuvert] = useState(false);

  const { data, isPending, error } = useQuery({
    queryKey: ["dossiers"],
    queryFn: dossiersApi.lister,
  });

  return (
    <>
      <EnTetePage
        surTitre="Portefeuille"
        titre="Dossiers"
        description="Chaque dossier est une entité juridique tenue pour ce tenant. Vous ne voyez que ceux sur lesquels vous êtes affecté."
        actions={
          peut("CREER_DOSSIER") ? (
            <button
              type="button"
              className={formulaireOuvert ? undefined : "principal"}
              onClick={() => setFormulaireOuvert((ouvert) => !ouvert)}
            >
              {formulaireOuvert ? "Annuler" : "Ouvrir un dossier"}
            </button>
          ) : undefined
        }
      />

      {formulaireOuvert && <FormulaireDeDossier surFermeture={() => setFormulaireOuvert(false)} />}

      {error != null && (
        <Message type="erreur" titre={error instanceof ErreurApi ? error.titre : "Erreur"}>
          <p style={{ margin: "6px 0 0" }}>{error.message}</p>
        </Message>
      )}

      {isPending && <Chargement quoi="les dossiers" />}

      {data != null && data.length === 0 && (
        <Vide>
          Aucun dossier accessible. Un collaborateur ne voit que les dossiers sur lesquels il est
          affecté.
        </Vide>
      )}

      {data != null && data.length > 0 && (
        <div className="tableau-conteneur">
          <table>
            <thead>
              <tr>
                <th>Raison sociale</th>
                <th>Forme</th>
                <th>Identifiant légal</th>
                <th>Référentiel</th>
                <th>Régime</th>
                <th>TVA</th>
                <th>Statut</th>
              </tr>
            </thead>
            <tbody>
              {data.map((dossier) => (
                <tr key={dossier.id}>
                  <td>
                    <Link to={`/dossiers/${dossier.id}`}>{dossier.raisonSociale}</Link>
                  </td>
                  <td className="discret-texte">{dossier.formeJuridique ?? "—"}</td>
                  <td className="nombre">{dossier.identifiants.principal}</td>
                  <td>{dossier.codeDuReferentiel}</td>
                  <td className="discret-texte">{dossier.regimeDImpositionLibelle}</td>
                  <td>
                    {dossier.tva.assujetti ? (
                      <Etiquette variante="info">{dossier.tva.periodicite}</Etiquette>
                    ) : (
                      <Etiquette variante="neutre">Non assujetti</Etiquette>
                    )}
                  </td>
                  <td>
                    <Etiquette variante={dossier.statut === "Actif" ? "succes" : "neutre"}>
                      {dossier.statut}
                    </Etiquette>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/**
 * Ouverture d'un dossier.
 *
 * Les champs d'identification changent avec le pays : l'ICE au Maroc, le SIREN
 * en France. Afficher les deux jeux ensemble laisserait croire qu'ils se
 * cumulent, alors qu'ils s'excluent.
 */
function FormulaireDeDossier({ surFermeture }: { surFermeture: () => void }) {
  const cache = useQueryClient();
  const [pays, setPays] = useState("MA");
  const [assujetti, setAssujetti] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);

  const { data: options } = useQuery({
    queryKey: ["dossiers-options"],
    queryFn: dossiersApi.options,
  });

  const creation = useMutation({
    mutationFn: (demande: CreationDeDossier) => dossiersApi.ouvrir(demande),
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: ["dossiers"] });
      surFermeture();
    },
    onError: (echec) =>
      setErreur(echec instanceof ErreurApi ? echec.message : "L'ouverture n'a pas abouti."),
  });

  function soumettre(evenement: React.FormEvent<HTMLFormElement>) {
    evenement.preventDefault();
    setErreur(null);
    const champs = new FormData(evenement.currentTarget);
    const texte = (nom: string) => {
      const valeur = champs.get(nom);
      return typeof valeur === "string" && valeur.trim() !== "" ? valeur.trim() : null;
    };

    creation.mutate({
      raisonSociale: texte("raisonSociale") ?? "",
      formeJuridique: texte("formeJuridique"),
      pays,
      ice: pays === "MA" ? texte("ice") : null,
      identifiantFiscal: pays === "MA" ? texte("identifiantFiscal") : null,
      registreDeCommerce: pays === "MA" ? texte("registreDeCommerce") : null,
      taxeProfessionnelle: pays === "MA" ? texte("taxeProfessionnelle") : null,
      affiliationCnss: pays === "MA" ? texte("affiliationCnss") : null,
      siren: pays === "FR" ? texte("siren") : null,
      siret: pays === "FR" ? texte("siret") : null,
      tvaIntracommunautaire: pays === "FR" ? texte("tvaIntracommunautaire") : null,
      codeApe: pays === "FR" ? texte("codeApe") : null,
      codeDuReferentiel: texte("codeDuReferentiel") ?? (pays === "MA" ? "PCGM" : "PCG-FR"),
      devise: texte("devise") ?? (pays === "MA" ? "MAD" : "EUR"),
      regimeDImposition: texte("regimeDImposition") ?? "",
      tvaAssujetti: assujetti,
      tvaPeriodicite: assujetti ? texte("tvaPeriodicite") : null,
      tvaRegime: assujetti ? texte("tvaRegime") : null,
      tvaProrata: assujetti ? texte("tvaProrata") : null,
    });
  }

  const regimes = options?.regimesDImposition[pays] ?? [];

  return (
    <form className="carte" onSubmit={soumettre}>
      <h2>Ouvrir un dossier</h2>

      {erreur != null && <Message type="erreur">{erreur}</Message>}

      <div className="champs">
        <div className="champ">
          <label htmlFor="pays">Pays</label>
          <select id="pays" value={pays} onChange={(e) => setPays(e.target.value)}>
            <option value="MA">Maroc</option>
            <option value="FR">France</option>
          </select>
        </div>

        <div className="champ">
          <label htmlFor="raisonSociale">Raison sociale</label>
          <input id="raisonSociale" name="raisonSociale" required />
        </div>

        <div className="champ">
          <label htmlFor="formeJuridique">Forme juridique</label>
          <input id="formeJuridique" name="formeJuridique" placeholder="SARL, SA…" />
        </div>

        {pays === "MA" ? (
          <>
            <div className="champ">
              <label htmlFor="ice">ICE</label>
              <input id="ice" name="ice" required placeholder="15 chiffres" />
            </div>
            <div className="champ">
              <label htmlFor="identifiantFiscal">Identifiant fiscal</label>
              <input id="identifiantFiscal" name="identifiantFiscal" />
            </div>
            <div className="champ">
              <label htmlFor="registreDeCommerce">Registre de commerce</label>
              <input id="registreDeCommerce" name="registreDeCommerce" />
            </div>
            <div className="champ">
              <label htmlFor="taxeProfessionnelle">Taxe professionnelle</label>
              <input id="taxeProfessionnelle" name="taxeProfessionnelle" />
            </div>
            <div className="champ">
              <label htmlFor="affiliationCnss">Affiliation CNSS</label>
              <input id="affiliationCnss" name="affiliationCnss" />
            </div>
          </>
        ) : (
          <>
            <div className="champ">
              <label htmlFor="siren">SIREN</label>
              <input id="siren" name="siren" required placeholder="9 chiffres" />
            </div>
            <div className="champ">
              <label htmlFor="siret">SIRET</label>
              <input id="siret" name="siret" placeholder="14 chiffres" />
            </div>
            <div className="champ">
              <label htmlFor="tvaIntracommunautaire">TVA intracommunautaire</label>
              <input id="tvaIntracommunautaire" name="tvaIntracommunautaire" />
            </div>
            <div className="champ">
              <label htmlFor="codeApe">Code APE</label>
              <input id="codeApe" name="codeApe" placeholder="6920Z" />
            </div>
          </>
        )}

        <div className="champ">
          <label htmlFor="codeDuReferentiel">Référentiel</label>
          <input
            id="codeDuReferentiel"
            name="codeDuReferentiel"
            defaultValue={pays === "MA" ? "PCGM" : "PCG-FR"}
            key={pays}
          />
        </div>

        <div className="champ">
          <label htmlFor="devise">Devise de tenue</label>
          <input id="devise" name="devise" defaultValue={pays === "MA" ? "MAD" : "EUR"} key={`d-${pays}`} />
        </div>

        <div className="champ">
          <label htmlFor="regimeDImposition">Régime d'imposition</label>
          <select id="regimeDImposition" name="regimeDImposition" required>
            {regimes.map((regime) => (
              <option key={regime.code} value={regime.code}>
                {regime.libelle}
              </option>
            ))}
          </select>
        </div>

        <div className="champ">
          <label htmlFor="tvaAssujetti">TVA</label>
          <select
            id="tvaAssujetti"
            value={assujetti ? "oui" : "non"}
            onChange={(e) => setAssujetti(e.target.value === "oui")}
          >
            <option value="oui">Assujetti</option>
            <option value="non">Non assujetti</option>
          </select>
        </div>

        {assujetti && (
          <>
            <div className="champ">
              <label htmlFor="tvaPeriodicite">Périodicité</label>
              <select id="tvaPeriodicite" name="tvaPeriodicite" required>
                {(options?.periodicitesDeTva ?? []).map((o) => (
                  <option key={o.code} value={o.code}>
                    {o.libelle}
                  </option>
                ))}
              </select>
            </div>
            <div className="champ">
              <label htmlFor="tvaRegime">Régime de TVA</label>
              <select id="tvaRegime" name="tvaRegime" required>
                {(options?.regimesDeTva ?? []).map((o) => (
                  <option key={o.code} value={o.code}>
                    {o.libelle}
                  </option>
                ))}
              </select>
              <div className="aide">Il commande la date d'exigibilité de la TVA.</div>
            </div>
            <div className="champ">
              <label htmlFor="tvaProrata">Prorata de déduction (%)</label>
              <input id="tvaProrata" name="tvaProrata" inputMode="decimal" placeholder="Sans objet si intégral" />
            </div>
          </>
        )}
      </div>

      <div className="barre-actions" style={{ marginTop: 18 }}>
        <button type="submit" className="principal" disabled={creation.isPending}>
          {creation.isPending ? "Ouverture…" : "Ouvrir le dossier"}
        </button>
        <button type="button" onClick={surFermeture}>
          Annuler
        </button>
      </div>
    </form>
  );
}
